import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate as useRouterNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Search, Sparkles, CornerDownLeft, ArrowLeft, BookOpen, Grid3x3,
  ClipboardList, AlertCircle, ChevronDown, Bookmark, BookmarkCheck, Settings,
  Compass, ArrowRight, Layers, ExternalLink, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/authStore';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────────
   NAVIGATE — Google-style guideline & process knowledge base.
   Landing: big greeting + centered search + Ask AI.
   Results: search summary + breadcrumb result cards.
   Detail:  full body render inline.
   ──────────────────────────────────────────────────────────────── */

const KIND_META = {
  Guideline: { icon: BookOpen,       label: 'Guideline',  tone: 'neutral' },
  Matrix:    { icon: Grid3x3,        label: 'Matrix',     tone: 'sky' },
  Process:   { icon: ClipboardList,  label: 'Process',    tone: 'gold' },
  Bulletin:  { icon: AlertCircle,    label: 'Bulletin',   tone: 'hot' },
  Overlay:   { icon: Layers,         label: 'Overlay',    tone: 'violet' },
};

/* Map a source string → a small colored pill (Rocket-style). */
function sourcePill(source) {
  if (!source) return null;
  if (source.toLowerCase().includes('fannie'))  return { label: 'Fannie',  className: 'bg-red-50 text-red-700 border-red-200' };
  if (source.toLowerCase().includes('freddie')) return { label: 'Freddie', className: 'bg-red-50 text-red-700 border-red-200' };
  if (source.toLowerCase().includes('fha'))     return { label: 'FHA',     className: 'bg-blue-50 text-blue-700 border-blue-200' };
  if (source.toLowerCase().includes(' va'))     return { label: 'VA',      className: 'bg-green-50 text-green-700 border-green-200' };
  if (source.toLowerCase().includes('usda'))    return { label: 'USDA',    className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (source.toLowerCase().includes('overlay')) return { label: 'Overlay', className: 'bg-violet-50 text-violet-700 border-violet-200' };
  return { label: source.split(' ')[0], className: 'bg-neutral-50 text-neutral-700 border-neutral-200' };
}

export default function Navigate() {
  const { user, isAdmin } = useAuth();
  const { id } = useParams();
  const router = useRouterNavigate();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState(''); // the query the results are ABOUT
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [favorites, setFavorites] = useState(() => new Set(readFavs()));
  const [programFilter, setProgramFilter] = useState('all'); // 'all' | 'Fannie Mae' | 'FHA' | ... | lender:LenderName
  const [lendersMenuOpen, setLendersMenuOpen] = useState(false);
  const [lenders, setLenders] = useState([]);
  const inputRef = useRef(null);

  // Prime the lender list once so the Lenders dropdown has real options.
  useEffect(() => {
    axios.get(`${API}/api/lenders`)
      .then((r) => setLenders(Array.isArray(r.data) ? r.data.filter((l) => !l.is_deleted) : []))
      .catch(() => setLenders([]));
  }, []);

  // Detail route — /navigate/:id — fetch the full body on land/direct link.
  useEffect(() => {
    if (!id) { setDetail(null); return; }
    axios.get(`${API}/api/guidelines/${id}`)
      .then((r) => setDetail(r.data))
      .catch(() => { toast.error('Guideline not found'); router('/navigate'); });
  }, [id, router]);

  const runSearch = async (queryString) => {
    if (!isAdmin) return; // LOs can see the page but not run searches yet.
    const trimmed = (queryString ?? q).trim();
    // Even a blank query is OK when a program chip is active — show all
    // guidelines for that program.
    const hasFilter = programFilter !== 'all';
    if (!trimmed && !hasFilter) {
      setResults([]);
      setSubmitted('');
      return;
    }
    setLoading(true);
    setSubmitted(trimmed || `all ${programFilter}`);
    try {
      const params = { limit: 50 };
      if (trimmed) params.q = trimmed;
      if (programFilter !== 'all') {
        if (programFilter.startsWith('lender:')) {
          params.lender = programFilter.slice(7);
          params.base_only = false;
        } else if (programFilter === 'Fannie Mae') {
          params.source = 'Fannie Mae Selling Guide 08/05/2026';
          params.program = 'Conventional';
        } else if (programFilter === 'Freddie Mac') {
          params.program = 'Conventional';
        } else {
          params.program = programFilter;
        }
      }
      const r = await axios.get(`${API}/api/guidelines`, { params });
      setResults(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Whenever a chip flips, re-run the current query so results update
  // in-place without the user having to hit Enter again.
  useEffect(() => {
    if (submitted || programFilter !== 'all') runSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programFilter]);

  const onKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch();
    } else if (e.key === 'Escape' && submitted) {
      setSubmitted('');
      setResults([]);
      setQ('');
    }
  };

  const askAi = () => {
    if (!q.trim()) {
      toast.message('Type a question first.', { description: 'Wingman will chat back on the Assistant panel.' });
      return;
    }
    // Stub for now — flags the concept without wiring Wingman here yet.
    toast.success('Navigate Assist coming soon', {
      description: 'This will route your question to Wingman with the guideline corpus attached.',
    });
  };

  const toggleFav = (gid) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      writeFavs(next);
      return next;
    });
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  // DETAIL VIEW ────────────────────────────────────────────────────
  if (id) {
    return (
      <DetailView
        guideline={detail}
        onBack={() => router('/navigate')}
        favorited={detail && favorites.has(detail.id)}
        onFav={() => detail && toggleFav(detail.id)}
      />
    );
  }

  // RESULTS VIEW ───────────────────────────────────────────────────
  if (submitted) {
    return (
      <ResultsView
        q={submitted}
        results={results}
        loading={loading}
        favorites={favorites}
        onFav={toggleFav}
        onClear={() => { setSubmitted(''); setResults([]); setQ(''); setTimeout(() => inputRef.current?.focus(), 0); }}
        onOpen={(gid) => router(`/navigate/${gid}`)}
        onEditQuery={(v) => { setQ(v); runSearch(v); }}
      />
    );
  }

  // LANDING VIEW — Editorial serif hero, program chips ────────────
  const FILTER_CHIPS = [
    { key: 'all',      label: 'All Guidelines' },
    { key: 'Fannie Mae', label: 'Fannie Mae', source: 'Fannie Mae Selling Guide 08/05/2026' },
    { key: 'Freddie Mac', label: 'Freddie Mac', source: 'Freddie Mac Selling Guide' },
    { key: 'FHA',      label: 'FHA' },
    { key: 'VA',       label: 'VA' },
    { key: 'USDA',     label: 'USDA' },
  ];
  return (
    <div className="min-h-[calc(100vh-140px)] flex flex-col items-center px-4 pt-16">
      {/* KINFOLK wordmark with gold underline */}
      <div className="flex flex-col items-center">
        <div className="text-[13px] tracking-[0.4em] font-semibold text-neutral-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          KINFOLK
        </div>
        <div className="mt-2 h-px w-14 bg-[hsl(var(--kf-gold))]" />
      </div>

      {/* NAVIGATE — huge editorial serif */}
      <h1
        className="mt-6 text-[64px] sm:text-[88px] lg:text-[104px] leading-none tracking-[0.06em] text-neutral-900"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
      >
        NAVIGATE
      </h1>

      {/* Tagline */}
      <div
        className="mt-4 text-[13px] sm:text-[14px] tracking-[0.32em] text-neutral-600 text-center"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        GUIDELINES. OVERLAYS. REAL ANSWERS.
      </div>

      {/* Search bar */}
      <div className="mt-10 w-full max-w-3xl">
        <div className={cn(
          "relative rounded-full bg-white shadow-[0_20px_60px_-24px_rgba(0,0,0,0.25),0_4px_12px_-4px_rgba(0,0,0,0.06)] transition-shadow flex items-center gap-2 pl-6 pr-2 py-2",
          isAdmin && "hover:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.3),0_6px_14px_-4px_rgba(0,0,0,0.08)] focus-within:shadow-[0_28px_70px_-24px_rgba(0,0,0,0.35)]",
        )}>
          <Search className="h-4 w-4 text-neutral-400 shrink-0" strokeWidth={2.4} />
          <input
            ref={inputRef}
            data-testid={isAdmin ? "navigate-search-input" : "navigate-search-coming-soon"}
            autoFocus={isAdmin}
            disabled={!isAdmin}
            value={isAdmin ? q : ''}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={isAdmin ? "Search guidelines, overlays, or ask a question..." : "COMING SOON"}
            className={cn(
              "flex-1 bg-transparent outline-none py-3 text-[15px] text-neutral-900",
              isAdmin
                ? "placeholder-neutral-400"
                : "placeholder-neutral-400 tracking-[0.32em] font-semibold text-center cursor-not-allowed",
            )}
          />
          <button
            data-testid="navigate-search-btn"
            onClick={() => isAdmin && runSearch()}
            disabled={!isAdmin}
            className={cn(
              "inline-flex items-center gap-2 rounded-full text-white pl-6 pr-5 py-2.5 text-[13.5px] font-semibold transition",
              isAdmin
                ? "bg-neutral-950 hover:bg-neutral-800"
                : "bg-neutral-300 cursor-not-allowed",
            )}
          >
            Search <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2 max-w-4xl">
        {FILTER_CHIPS.map((c) => {
          const active = programFilter === c.key;
          return (
            <button
              key={c.key}
              data-testid={`navigate-chip-${c.key.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => setProgramFilter(c.key)}
              className={cn(
                'inline-flex items-center rounded-full px-5 py-2 text-[13px] font-semibold transition',
                active
                  ? 'bg-neutral-950 text-white shadow-[0_4px_16px_-6px_rgba(0,0,0,0.3)]'
                  : 'bg-white text-neutral-800 border hairline hover:border-neutral-900',
              )}
            >
              {c.label}
            </button>
          );
        })}
        {/* Lenders dropdown */}
        <LendersFilterInner
          value={programFilter}
          onChange={setProgramFilter}
          open={lendersMenuOpen}
          setOpen={setLendersMenuOpen}
          lenders={lenders}
        />
      </div>
    </div>
  );
}

/* ── Results view ─────────────────────────────────────────────────── */
function ResultsView({ q, results, loading, favorites, onFav, onClear, onOpen, onEditQuery }) {
  const [showSummary, setShowSummary] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const visible = results.slice(0, pageSize);
  const empty = !loading && results.length === 0;
  const [localQ, setLocalQ] = useState(q);
  useEffect(() => setLocalQ(q), [q]);

  return (
    <div className="pb-16">
      {/* Compact search bar at top */}
      <div className="sticky top-0 z-10 -mx-4 lg:-mx-8 px-4 lg:px-8 pt-2 pb-3 bg-white/80 backdrop-blur-md border-b hairline">
        <div className="flex items-center gap-3 max-w-4xl">
          <button
            onClick={onClear}
            data-testid="navigate-back-to-home"
            className="h-9 w-9 rounded-full border hairline bg-white flex items-center justify-center hover:border-neutral-900 transition shrink-0"
            aria-label="Back to Navigate home"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 relative rounded-full border-2 border-neutral-200 focus-within:border-neutral-900 bg-white flex items-center gap-2 pl-4 pr-2 py-1">
            <Search className="h-4 w-4 text-neutral-400" strokeWidth={2.4} />
            <input
              data-testid="navigate-results-search"
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onEditQuery(localQ); }}
              className="flex-1 bg-transparent outline-none py-1.5 text-[13.5px] text-neutral-900"
            />
            <button
              onClick={() => onEditQuery(localQ)}
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral-950 text-white px-3 py-1.5 text-[11.5px] font-bold hover:bg-neutral-800 transition"
            >
              <Sparkles className="h-3 w-3 text-[hsl(var(--kf-gold))]" /> Ask AI
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 max-w-4xl">
        {/* Search summary card */}
        {empty ? (
          <SummaryCard tone="empty" open={showSummary} onToggle={() => setShowSummary((v) => !v)}>
            <p className="text-[13px] text-neutral-800">I don't see exact matches for your search. You can either:</p>
            <ul className="mt-2 space-y-1 text-[12.5px] text-neutral-600 list-disc pl-5">
              <li>Refine your search terms by using alternative keywords, more specific phrases, or broader concepts related to your topic</li>
              <li>Review the results displayed below, as they may still contain relevant information or related topics</li>
              <li>Try one of the suggested searches, or click <b className="text-neutral-800">Ask AI</b> to have Navigate Assist reason through it</li>
            </ul>
          </SummaryCard>
        ) : (
          <SummaryCard tone="ok" open={showSummary} onToggle={() => setShowSummary((v) => !v)}>
            <p className="text-[13px] text-neutral-800">
              Found <b className="text-neutral-900">{results.length}</b>{' '}
              {results.length === 1 ? 'guideline' : 'guidelines'} matching{' '}
              <b className="text-neutral-900">"{q}"</b>.
            </p>
            <p className="mt-1 text-[12px] text-neutral-500">
              Newest first. Click any result to read the full body. Click{' '}
              <b className="text-neutral-700">Ask AI</b> up top to have Navigate Assist synthesize an answer.
            </p>
          </SummaryCard>
        )}

        {/* Results header */}
        <div className="mt-4 text-[11.5px] text-neutral-500">
          Showing {Math.min(pageSize, results.length)} of {results.length} results
        </div>

        {/* Result cards */}
        <div className="mt-2 space-y-2">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-neutral-100 animate-pulse" />
            ))
          ) : visible.map((r) => (
            <ResultCard
              key={r.id}
              guideline={r}
              q={q}
              favorited={favorites.has(r.id)}
              onFav={() => onFav(r.id)}
              onOpen={() => onOpen(r.id)}
            />
          ))}
        </div>

        {/* Show more */}
        {results.length > pageSize && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setPageSize((n) => n + 10)}
              data-testid="navigate-show-more"
              className="inline-flex items-center gap-1.5 rounded-full border hairline bg-white px-4 py-2 text-[12.5px] font-semibold text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition"
            >
              Show more <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Summary card ────────────────────────────────────────────────── */
function SummaryCard({ tone = 'ok', open, onToggle, children }) {
  return (
    <div
      data-testid="navigate-summary-card"
      className={cn(
        'rounded-2xl border p-4',
        tone === 'empty' ? 'bg-neutral-50 border-neutral-200' : 'bg-white hairline',
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('h-6 w-6 rounded-full flex items-center justify-center shrink-0',
          tone === 'empty' ? 'bg-white border hairline text-neutral-500' : 'bg-neutral-950 text-[hsl(var(--kf-gold))]')}>
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-bold text-neutral-900 mb-1">Search summary</h3>
          {open ? children : (
            <div className="text-[12px] text-neutral-500 truncate">Summary collapsed — click to expand.</div>
          )}
          <button
            onClick={onToggle}
            className="mt-2 inline-flex items-center gap-1 rounded-full border hairline bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition"
          >
            {open ? 'Show less' : 'Show more'} <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Result card ─────────────────────────────────────────────────── */
function ResultCard({ guideline, q, favorited, onFav, onOpen }) {
  const meta = KIND_META[guideline.kind] || KIND_META.Guideline;
  const Icon = meta.icon;

  // Prefer the backend-supplied snippet (windowed around the query hit),
  // otherwise fall back to the curated summary or the body head.
  const snippet =
    guideline.snippet
    || guideline.summary
    || (guideline.body || '').replace(/[#*`_]/g, '').slice(0, 240);

  return (
    <div
      data-testid={`navigate-result-${guideline.id}`}
      className="group rounded-xl border hairline bg-white p-4 hover:bg-neutral-50 transition-colors flex items-start gap-4"
    >
      {/* Left rail — kind badge */}
      <button
        type="button"
        onClick={onOpen}
        className="shrink-0 flex flex-col items-center gap-1 text-neutral-500 hover:text-neutral-900 transition"
        aria-label={`Open ${meta.label}`}
      >
        <div className="h-11 w-8 rounded-md border-2 border-neutral-300 flex items-center justify-center bg-white">
          <Icon className="h-4 w-4" strokeWidth={2.4} />
        </div>
        <div className="text-[9.5px] uppercase tracking-[0.14em] font-bold underline decoration-neutral-300 underline-offset-2">
          {meta.label}
        </div>
      </button>

      {/* Body */}
      <div className="flex-1 min-w-0" onClick={onOpen} role="button">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500 truncate">
          <span className="truncate">{guideline.category || 'Uncategorized'}</span>
          {guideline.section_code && (
            <span className="inline-flex items-center rounded border hairline bg-neutral-50 px-1.5 py-0.5 font-bold text-neutral-700 normal-case tracking-normal">
              {guideline.section_code}
            </span>
          )}
        </div>
        <h3 className="mt-0.5 text-[15px] font-bold tracking-tight text-neutral-900 hover:underline underline-offset-2 decoration-2 decoration-[hsl(var(--kf-gold))] cursor-pointer">
          <Highlight text={guideline.title} q={q} />
        </h3>
        <p className="mt-1 text-[12.5px] text-neutral-600 leading-relaxed line-clamp-3">
          <Highlight text={snippet} q={q} />
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {(() => {
            const pill = sourcePill(guideline.source);
            return pill && (
              <span className={cn('inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-bold', pill.className)}>
                {pill.label}
              </span>
            );
          })()}
          {guideline.tags?.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded border hairline bg-neutral-50 text-neutral-600">
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Right rail — actions */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onFav(); }}
        data-testid={`navigate-fav-${guideline.id}`}
        className={cn(
          'shrink-0 h-8 w-8 rounded-full border hairline flex items-center justify-center transition',
          favorited ? 'bg-[hsl(var(--kf-gold-soft))] border-[hsl(var(--kf-gold))] text-neutral-900' : 'bg-white text-neutral-400 hover:text-neutral-900',
        )}
        aria-label={favorited ? 'Unfavorite' : 'Favorite'}
      >
        {favorited ? <BookmarkCheck className="h-4 w-4" strokeWidth={2.4} /> : <Bookmark className="h-4 w-4" strokeWidth={2.4} />}
      </button>
    </div>
  );
}

/* ── Highlight matches in text ────────────────────────────────────── */
function Highlight({ text, q }) {
  if (!q || !text) return <>{text}</>;
  const parts = String(text).split(new RegExp(`(${escapeRegex(q)})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-[hsl(var(--kf-gold-soft))] text-neutral-900 rounded px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/* ── Detail view ─────────────────────────────────────────────────── */
function DetailView({ guideline, onBack, favorited, onFav }) {
  const [overlays, setOverlays] = useState([]);
  const [overlaysLoading, setOverlaysLoading] = useState(false);
  const [activeLender, setActiveLender] = useState('base'); // 'base' | lender name
  // 'simple' shows the LLM-rewritten Rocket-style body; 'original' shows raw source.
  const [bodyMode, setBodyMode] = useState('simple');

  useEffect(() => {
    if (!guideline?.id) return;
    setOverlaysLoading(true);
    axios.get(`${API}/api/guidelines/${guideline.id}/overlays`)
      .then((r) => setOverlays(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOverlays([]))
      .finally(() => setOverlaysLoading(false));
  }, [guideline?.id]);

  if (!guideline) {
    return (
      <div className="pb-16">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-500 hover:text-neutral-900 mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Navigate
        </button>
        <div className="h-40 rounded-xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }

  const meta = KIND_META[guideline.kind] || KIND_META.Guideline;
  const Icon = meta.icon;
  const activeOverlay = activeLender === 'base'
    ? null
    : overlays.find((o) => o.lender === activeLender);

  return (
    <div className="pb-16 max-w-5xl">
      <button
        onClick={onBack}
        data-testid="navigate-detail-back"
        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-500 hover:text-neutral-900 mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Navigate
      </button>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        {/* HEADER */}
        <div className="px-6 py-5 border-b hairline">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500">
                {(guideline.category || '').split('/').map((seg, i, arr) => (
                  <React.Fragment key={i}>
                    <span className="truncate">{seg.trim()}</span>
                    {i < arr.length - 1 && <ChevronRight className="h-3 w-3 text-neutral-300" />}
                  </React.Fragment>
                ))}
              </div>
              <h1 className="mt-1 text-2xl sm:text-3xl font-black tracking-tight text-neutral-900">
                {guideline.title}
              </h1>
              {guideline.summary && (
                <p className="mt-2 text-[13px] text-neutral-600 max-w-2xl leading-relaxed">{guideline.summary}</p>
              )}
              <div className="mt-3 flex items-center flex-wrap gap-2 text-[11px] text-neutral-500">
                <span className="inline-flex items-center gap-1 rounded-md border hairline bg-neutral-50 px-1.5 py-0.5">
                  <Icon className="h-3 w-3" strokeWidth={2.4} /> {meta.label}
                </span>
                {guideline.section_code && (
                  <span className="inline-flex items-center rounded-md border hairline bg-neutral-50 px-1.5 py-0.5 font-bold text-neutral-700">
                    {guideline.section_code}
                  </span>
                )}
                {guideline.program && (
                  <span className="inline-flex items-center rounded-md border hairline bg-neutral-50 px-1.5 py-0.5">
                    {guideline.program}
                  </span>
                )}
                {(() => {
                  const pill = sourcePill(guideline.source);
                  return pill && (
                    <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-bold', pill.className)}>
                      {pill.label}
                      {guideline.source_url && (
                        <a
                          href={guideline.source_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-0.5 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </span>
                  );
                })()}
                <span className="tabular-nums">Updated {formatDate(guideline.updated_at)}</span>
              </div>
            </div>
            <button
              onClick={onFav}
              data-testid="navigate-detail-fav"
              className={cn(
                'h-9 w-9 rounded-full border hairline flex items-center justify-center shrink-0 transition',
                favorited ? 'bg-[hsl(var(--kf-gold-soft))] border-[hsl(var(--kf-gold))]' : 'bg-white text-neutral-500 hover:text-neutral-900',
              )}
              aria-label={favorited ? 'Unfavorite' : 'Favorite'}
            >
              {favorited ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* LENDER OVERLAYS PICKER — only shown when overlays exist */}
        {overlays.length > 0 && (
          <div className="px-6 py-3 border-b hairline bg-neutral-50">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500 font-bold">
                <Layers className="h-3 w-3" strokeWidth={2.5} /> View overlays
              </div>
              <LenderChip
                active={activeLender === 'base'}
                onClick={() => setActiveLender('base')}
                label="Base (Fannie Mae)"
                badge={null}
              />
              {overlays.map((o) => (
                <LenderChip
                  key={o.id}
                  active={activeLender === o.lender}
                  onClick={() => setActiveLender(o.lender)}
                  label={o.lender}
                  badge="Overlay"
                />
              ))}
            </div>
          </div>
        )}

        {/* BODY — either the base or the selected overlay */}
        <div className="px-6 py-5">
          {activeOverlay ? (
            <div>
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] text-violet-900">
                <Layers className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                <span>
                  Viewing <b>{activeOverlay.lender}</b>'s overlay on {guideline.section_code || 'this section'}.
                  Base Fannie language is preserved above.
                </span>
              </div>
              <MarkdownBody body={activeOverlay.body_simple || activeOverlay.body} />
            </div>
          ) : (
            <>
              {/* Simple vs original toggle — only shown when we HAVE a simple version. */}
              {guideline.body_simple && (
                <div className="mb-4 inline-flex items-center gap-1 rounded-full border hairline bg-neutral-50 p-1 text-[11.5px] font-semibold">
                  <button
                    type="button"
                    onClick={() => setBodyMode('simple')}
                    data-testid="navigate-body-mode-simple"
                    className={cn(
                      'rounded-full px-3 py-1 transition',
                      bodyMode === 'simple' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800',
                    )}
                  >
                    Plain English
                  </button>
                  <button
                    type="button"
                    onClick={() => setBodyMode('original')}
                    data-testid="navigate-body-mode-original"
                    className={cn(
                      'rounded-full px-3 py-1 transition',
                      bodyMode === 'original' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800',
                    )}
                  >
                    Original {guideline.source ? '· Fannie Mae' : ''}
                  </button>
                </div>
              )}
              <MarkdownBody body={bodyMode === 'original' || !guideline.body_simple ? guideline.body : guideline.body_simple} />
            </>
          )}
        </div>

        {/* OVERLAY DOCTORS — when there are overlays, show a compact list at
            bottom so LOs see EVERY lender variance without hopping tabs. */}
        {overlays.length > 0 && activeLender === 'base' && (
          <div className="px-6 pb-5">
            <div className="rounded-xl border hairline bg-neutral-50 p-4">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-neutral-500 mb-2">
                Lenders that overlay this section
              </div>
              <div className="divide-y hairline">
                {overlays.map((o) => (
                  <div key={o.id} className="py-2 flex items-start gap-3">
                    <div className="h-6 w-6 shrink-0 rounded-full bg-neutral-900 text-white text-[9px] font-bold flex items-center justify-center">
                      {o.lender.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-bold text-neutral-900">{o.lender}</div>
                      <div className="text-[11.5px] text-neutral-600 line-clamp-2">{o.summary}</div>
                    </div>
                    <button
                      onClick={() => setActiveLender(o.lender)}
                      className="text-[11px] font-bold text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1 shrink-0"
                    >
                      View <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LenderChip({ active, onClick, label, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`overlay-chip-${label}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold transition',
        active
          ? 'bg-neutral-900 text-white'
          : 'bg-white border hairline text-neutral-700 hover:border-neutral-900 hover:text-neutral-900',
      )}
    >
      {label}
      {badge && (
        <span className={cn(
          'text-[9px] px-1 py-px rounded',
          active ? 'bg-white/20 text-white' : 'bg-violet-100 text-violet-700',
        )}>{badge}</span>
      )}
    </button>
  );
}

function MarkdownBody({ body }) {
  if (!body) return <em className="text-neutral-400 text-[13px]">No body content yet.</em>;
  return (
    <div className="prose prose-sm max-w-none text-[13.5px] text-neutral-800 leading-relaxed
                    prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-neutral-900
                    prose-h1:text-[20px] prose-h2:text-[15px] prose-h2:mt-4 prose-h2:mb-2
                    prose-h3:text-[13.5px]
                    prose-strong:text-neutral-900 prose-strong:font-bold
                    prose-code:text-neutral-900 prose-code:bg-neutral-100 prose-code:px-1 prose-code:rounded prose-code:text-[12px] prose-code:before:content-none prose-code:after:content-none
                    prose-table:text-[12.5px]
                    prose-th:bg-neutral-50 prose-th:font-bold prose-th:text-left prose-th:text-neutral-700 prose-th:uppercase prose-th:text-[10.5px] prose-th:tracking-wider prose-th:py-2 prose-th:px-3
                    prose-td:py-2 prose-td:px-3 prose-td:border-neutral-100
                    prose-a:text-neutral-900 prose-a:underline prose-a:underline-offset-2 prose-a:decoration-[hsl(var(--kf-gold))]
                    prose-ul:my-2 prose-li:my-0.5">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── Favorites localStorage ──────────────────────────────────────── */
const FAV_KEY = 'kf:navigate:favs';
function readFavs() {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function writeFavs(set) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...set])); } catch {}
}

/* ── Lenders dropdown filter chip ─────────────────────────────────── */
function LendersFilterInner({ value, onChange, open, setOpen, lenders }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, setOpen]);

  const activeLender = value?.startsWith('lender:') ? value.slice(7) : null;
  const label = activeLender || 'Lenders';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="navigate-chip-lenders"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[13px] font-semibold transition',
          activeLender
            ? 'bg-neutral-950 text-white shadow-[0_4px_16px_-6px_rgba(0,0,0,0.3)]'
            : 'bg-white text-neutral-800 border hairline hover:border-neutral-900',
        )}
      >
        {label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          data-testid="navigate-lenders-menu"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 w-[240px] rounded-2xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="max-h-[340px] overflow-y-auto py-1">
            {lenders.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-neutral-500 italic">No lenders added yet.</div>
            )}
            {lenders.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => { onChange(`lender:${l.name}`); setOpen(false); }}
                className={cn(
                  'w-full text-left px-3 py-2 text-[12.5px] font-semibold flex items-center gap-2 transition',
                  activeLender === l.name ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                <span className="h-5 w-5 rounded-full bg-neutral-900 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                  {l.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                </span>
                <span className="truncate">{l.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

